import React, {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {Alert, Box, Button, CardHeader, Chip, CircularProgress, Link as MuiLink, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, Typography} from "@mui/material";
import {Add, Refresh} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {ContentContainer} from "../components/common/content-container";
import {useUser} from "../providers/user-context";
import {courierName} from "../data-tables-distributor/orders/courier-api";
import {canManageCouriers} from "../courier/courier-api";
import {availabilityLabel} from "../courier/courier-profile-form";
import CourierManagementLayout from "../courier/courier-management-layout";
import CreateCourierDialog from "../courier/create-courier-dialog";
import useCourierData from "../courier/use-courier-data";

function CourierList() {
    const {t} = useTranslation();
    const {tenantId, userRole} = useUser();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [createOpen, setCreateOpen] = useState(false);
    const {data, loading, error, refresh} = useCourierData(tenantId, null, page, size);

    useEffect(() => {
        if (data && page > Math.max(0, data.totalPages - 1)) setPage(Math.max(0, data.totalPages - 1));
    }, [data, page]);

    return <>
        <CardHeader title={t("CourierProfiles")} subheader={t("CourierManagementDescription")}
                    action={<Button startIcon={<Refresh/>} variant="outlined" disabled={loading} onClick={refresh}>
                        {t("Refresh")}</Button>}/>
        {canManageCouriers(userRole) && <Box sx={{display: "flex", justifyContent: "flex-end", mb: 2}}>
            <Button variant="contained" startIcon={<Add/>} onClick={() => setCreateOpen(true)}>{t("CreateCourierProfile")}</Button>
        </Box>}
        {error ? <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{t("Retry")}</Button>}>
            {t(error)}</Alert> : <ContentContainer aria-busy={loading}>
            {loading ? <Box sx={{p: 5, textAlign: "center"}}><CircularProgress aria-label={t("LoadingCouriers")}/></Box> :
                data.content.length === 0 ? <Typography sx={{p: 4, textAlign: "center"}}>{t("NoCourierProfiles")}</Typography> :
                    <TableContainer><Table aria-label={t("CourierManagement")}>
                        <TableHead><TableRow>
                            {["CourierUser", "CourierAvailability", "Status", "CourierActiveOrders", "CourierRemainingCapacity",
                                "CourierProfile"].map(label => <TableCell key={label}>{t(label)}</TableCell>)}
                        </TableRow></TableHead>
                        <TableBody>{data.content.map(profile => <TableRow key={profile.id}>
                            <TableCell><MuiLink component={Link} to={`/distributor/users/couriers/${profile.id}`}
                                                underline="hover" sx={{fontWeight: 600}}>{courierName(profile)}</MuiLink>
                                <Typography variant="body2" color="text.secondary">
                                {profile.userEmail}</Typography></TableCell>
                            <TableCell><Chip size="small" color={profile.availability === "AVAILABLE" ? "success" : "default"}
                                             label={t(availabilityLabel(profile.availability))}/></TableCell>
                            <TableCell><Chip size="small" variant="outlined" label={t(profile.active ? "Active" : "Inactive")}/></TableCell>
                            <TableCell>{profile.activeOrderCount} / {profile.maxActiveOrders}</TableCell>
                            <TableCell>{profile.remainingCapacity}</TableCell>
                            <TableCell><Button component={Link} to={`/distributor/users/couriers/${profile.id}`}
                                aria-label={`${t("ViewCourierProfile")}: ${courierName(profile)}`}>{t("ViewCourierProfile")}</Button></TableCell>
                        </TableRow>)}</TableBody>
                    </Table></TableContainer>}
            <TablePagination component="div" count={data?.totalElements || 0} page={loading ? 0 : page}
                             rowsPerPage={size} rowsPerPageOptions={[10, 20, 50, 100]} disabled={loading}
                             labelRowsPerPage={t("CouriersPerPage")}
                             labelDisplayedRows={({from, to, count}) => t("UserPageRange", {from, to, count})}
                             getItemAriaLabel={type => t(type === "next" ? "NextCouriersPage" : "PreviousCouriersPage")}
                             onPageChange={(_, value) => setPage(value)}
                             onRowsPerPageChange={event => { setSize(Number(event.target.value)); setPage(0); }}/>
        </ContentContainer>}
        {createOpen && canManageCouriers(userRole) && <CreateCourierDialog tenantId={tenantId} onClose={() => setCreateOpen(false)}
            onCreated={profile => navigate(`/distributor/users/couriers/${profile.id}`, {state: {courierCreated: true}})}/>}
    </>;
}

export default function CourierProfilesPage(props) {
    return <CourierManagementLayout {...props} activeTab="profiles"><CourierList/></CourierManagementLayout>;
}
